import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, X, Pencil, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import api from '../lib/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' });
};
const money = (n) => `S/ ${Number(n || 0).toFixed(2)}`;

function StateBadge({ row }) {
  if (!row.next_due_date) return <Badge variant="outline">Sin cobro</Badge>;
  if (row.pending_payment) return <Badge className="bg-blue-600">Por verificar</Badge>;
  if (row.level === 'late') return <Badge variant="destructive">Atrasado {row.days_overdue} d</Badge>;
  if (row.level === 'overdue') return <Badge className="bg-amber-500">Atrasado {row.days_overdue} d</Badge>;
  if (row.level === 'due') return <Badge className="bg-amber-400 text-black">Vence hoy</Badge>;
  return <Badge className="bg-emerald-600">Al día</Badge>;
}

const PAY_LABEL = { reported: 'Por verificar', confirmed: 'Confirmado', rejected: 'Rechazado' };

export default function SubscriptionsPage() {
  const [rows, setRows] = useState([]);
  const [payments, setPayments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount: '50', next_due_date: '', notes: '' });

  const load = async () => {
    try {
      const [s, p] = await Promise.all([api.get('/subscriptions'), api.get('/subscriptions/payments')]);
      setRows(s.data);
      setPayments(p.data);
    } catch {
      toast.error('No se pudieron cargar las suscripciones');
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      amount: String(row.amount ?? 50),
      next_due_date: row.next_due_date || '',
      notes: row.notes || '',
    });
  };

  const save = async () => {
    try {
      await api.put(`/subscriptions/${editing.doctor_user_id}`, {
        amount: Number(form.amount || 0),
        next_due_date: form.next_due_date || null,
        notes: form.notes || null,
      });
      toast.success('Guardado');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/subscriptions/payments/${id}/${action}`);
      toast.success(action === 'confirm' ? 'Pago confirmado' : 'Aviso reactivado para el médico');
      load();
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  const pending = payments.filter((p) => p.status === 'reported');
  const now = new Date();
  const monthTotal = payments
    .filter((p) => p.status === 'confirmed' && p.reviewed_at &&
      new Date(p.reviewed_at).getMonth() === now.getMonth() &&
      new Date(p.reviewed_at).getFullYear() === now.getFullYear())
    .reduce((a, p) => a + Number(p.amount), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Suscripciones</h1>
        <p className="text-muted-foreground">Cuota mensual de cada médico, pagada por Yape.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Por verificar</p>
          <p className="text-2xl font-bold">{pending.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Con aviso activo</p>
          <p className="text-2xl font-bold">{rows.filter((r) => r.show_reminder).length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Confirmado este mes</p>
          <p className="text-2xl font-bold">{money(monthTotal)}</p>
        </CardContent></Card>
      </div>

      {pending.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Pagos por verificar</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {pending.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
                <div className="text-sm">
                  <p className="font-medium">{p.full_name}</p>
                  <p className="text-muted-foreground">
                    {money(p.amount)} · período {fmtDate(p.period_due_date)} · avisó {fmtDate(p.reported_at)}
                    {p.operation_number ? ` · Op. ${p.operation_number}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => act(p.id, 'confirm')}><Check className="w-4 h-4 mr-1" />Confirmar</Button>
                  <Button size="sm" variant="outline" onClick={() => act(p.id, 'reject')}><X className="w-4 h-4 mr-1" />Rechazar</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Médicos</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-4">Médico</th>
                <th className="py-2 pr-4">Monto</th>
                <th className="py-2 pr-4">Próximo pago</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.doctor_user_id} className="border-b last:border-0">
                  <td className="py-2 pr-4">
                    <p className="font-medium">{r.full_name}</p>
                    <p className="text-xs text-muted-foreground">{r.email}</p>
                  </td>
                  <td className="py-2 pr-4">{r.has_subscription ? money(r.amount) : '—'}</td>
                  <td className="py-2 pr-4">{fmtDate(r.next_due_date)}</td>
                  <td className="py-2 pr-4"><StateBadge row={r} /></td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Historial de pagos</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {payments.filter((p) => p.status !== 'reported').length === 0 && (
            <p className="text-sm text-muted-foreground">Todavía no hay pagos revisados.</p>
          )}
          {payments.filter((p) => p.status !== 'reported').map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 py-2 text-sm">
              <span>
                <b>{p.full_name}</b> · {money(p.amount)} · período {fmtDate(p.period_due_date)}
                {p.operation_number ? ` · Op. ${p.operation_number}` : ''}
              </span>
              <span className="flex items-center gap-2">
                <Badge variant={p.status === 'rejected' ? 'destructive' : 'secondary'}>{PAY_LABEL[p.status]}</Badge>
                {p.status === 'confirmed' && (
                  <Button size="sm" variant="ghost" title="Era erróneo: reactivar aviso" onClick={() => act(p.id, 'reject')}>
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing?.full_name}</DialogTitle>
            <DialogDescription>
              El aviso aparece desde la fecha de pago. Cada mes se repite el mismo día.
              Deja la fecha vacía para no cobrarle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Monto mensual (S/)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label>Próxima fecha de pago</Label>
              <Input type="date" value={form.next_due_date}
                onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} />
            </div>
            <div>
              <Label>Nota interna</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save}>Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
